import { useCallback, useMemo } from 'react';
import { FCX, ID, Tooltip } from '@datassential/platform-ui-lib';
import './ComparisonBarChart.scss';

interface ComparisonChartData {
    mainTitle: string;
    compareTitle: string;
    mainTotal: number;
    compareTotal: number;
    values: Array<{
        id: ID;
        title: string;
        mainValue: number;
        compareValue: number;
    }>;
}

const ComparisonBarChart: FCX<{ data: ComparisonChartData }> = ({ data }) => {
    const maxValue = useMemo(
        (): number => {
            return Math.max(
                1,
                ...data.values.map(v => v.mainValue),
                ...data.values.map(v => v.compareValue),
            );
        },
        [data]
    );

    const getBarHeightPct = useCallback(
        (value: number): string => {
            return `${(Math.round(value) / maxValue) * 100}%`;
        },
        [maxValue],
    );

    return (
        <div className="ComparisonBarChart">
            <div className="ComparisonBarChart__legend">
                <div className="ComparisonBarChart__legend-keys">
                    <div className="ComparisonBarChart__legend-item">
                        <div className="ComparisonBarChart__legend-color is-main"/>
                        <span className="ComparisonBarChart__legend-title">{data.mainTitle}</span>
                    </div>
                    <div className="ComparisonBarChart__legend-item">
                        <div className="ComparisonBarChart__legend-color is-compare"/>
                        <span className="ComparisonBarChart__legend-title">{data.compareTitle}</span>
                    </div>
                </div>
                <div className="ComparisonBarChart__legend-totals">
                    <div className="ComparisonBarChart__legend-totals-title">Total No. of Menu Launches</div>
                    <div className="ComparisonBarChart__legend-totals-item">
                        <div className="ComparisonBarChart__legend-totals-count is-main">
                            {data.mainTotal}
                        </div>
                        <span className="ComparisonBarChart__legend-totals-text">{data.mainTitle}</span>
                    </div>
                    <div className="ComparisonBarChart__legend-totals-item">
                        <div className="ComparisonBarChart__legend-totals-count is-compare">
                            {data.compareTotal}
                        </div>
                        <span className="ComparisonBarChart__legend-totals-text">{data.compareTitle} Average</span>
                    </div>
                </div>
            </div>
            <div className="ComparisonBarChart__content">
                {data.values.map((row) => (
                    <div
                        key={row.id}
                        className="ComparisonBarChart__item"
                        data-tooltip-id={`comparison-bar-chart-${row.id}`}
                    >
                        <div
                            className="ComparisonBarChart__bar"
                            style={{ height: getBarHeightPct(row.mainValue) }}
                        />
                        <div
                            className="ComparisonBarChart__compare-bar"
                            style={{ bottom: getBarHeightPct(row.compareValue) }}
                        />
                        <div className="ComparisonBarChart__bar-label">
                            <span className="ComparisonBarChart__bar-label-text">{row.title}</span>
                        </div>
                        <div
                            className="ComparisonBarChart__value-label"
                            style={{ bottom: getBarHeightPct(row.mainValue) }}
                        >
                            {row.mainValue}
                        </div>
                        <Tooltip
                            id={`comparison-bar-chart-${row.id}`}
                            place="right"
                            float
                            style={{
                                animation: 'none',
                                zIndex: 3,
                                transition: 'none',
                            }}
                            offset={20}
                        >
                            <div className="ComparisonBarChart__tooltip">
                                <div className="ComparisonBarChart__tooltip-title">
                                    {row.title}
                                </div>
                                <div className="ComparisonBarChart__tooltip-row">
                                    <div>{data.mainTitle}</div>
                                    <div className="ComparisonBarChart__tooltip-color is-main"/>
                                    <div className="ComparisonBarChart__tooltip-row-value">
                                        {row.mainValue || 0}
                                    </div>
                                </div>
                                <div className="ComparisonBarChart__tooltip-row">
                                    <div>{data.compareTitle}</div>
                                    <div className="ComparisonBarChart__tooltip-color is-compare"/>
                                    <div className="ComparisonBarChart__tooltip-row-value">
                                        {row.compareValue || 0}
                                    </div>
                                </div>
                            </div>
                        </Tooltip>
                    </div>
                ))}
            </div>
        </div>
    );
};

export default ComparisonBarChart;
